// Suite sign-in contract. Keep this module identical in CRM, Workflow and CMS.
export type SuiteProduct = 'crm' | 'workflow' | 'cms';
type Store = Pick<Storage, 'getItem' | 'removeItem'>;
type RedirectOptions = { state: { returnTo: string }; prompt?: 'login' };
const products = { crm: { name: 'CRM', home: '/' }, workflow: { name: 'Workflow', home: '/dashboard' }, cms: { name: 'CMS', home: '/content' } };
const pending = new Map<SuiteProduct, Promise<void>>();

export function suiteReturnPath(value: unknown, fallback: string): string {
  if (typeof value !== 'string' || !value.startsWith('/') || value.startsWith('//') || value.includes('\\') || /\s/.test(value)) return fallback;
  let pathname: string;
  try { pathname = decodeURIComponent(value.split(/[?#]/)[0]).replace(/\/+$/, '').toLowerCase(); }
  catch { return fallback; }
  if (pathname.startsWith('//') || pathname.includes('\\') || /[\u0000-\u0020\u007f]/.test(pathname)) return fallback;
  return ['/signin', '/auth/callback', '/select-business', '/select-site'].includes(pathname) ? fallback : value;
}

export function suiteSignInState(product: SuiteProduct, local: Store = localStorage, session: Store = sessionStorage) {
  const rejected = session.getItem(`${product}-session-rejected`) === 'true';
  return {
    signedOut: local.getItem(`${product}-explicit-signout`) === 'true',
    rejected,
    error: session.getItem(`${product}-auth-error`) || (rejected ? `Your ${products[product].name} session could not be verified. Sign in again to continue.` : ''),
  };
}

// Interactive retry clears the stop markers. A rejected token requires a fresh
// Accounts login rather than silently repeating the same rejected SSO session.
export function beginSuiteSignIn(product: SuiteProduct, redirect: (options: RedirectOptions) => Promise<unknown>,
  requestedPath: unknown, local: Store = localStorage, session: Store = sessionStorage): Promise<void> {
  const existing = pending.get(product);
  if (existing) return existing;
  const { rejected } = suiteSignInState(product, local, session);
  local.removeItem(`${product}-explicit-signout`);
  session.removeItem(`${product}-session-rejected`);
  session.removeItem(`${product}-auth-error`);
  const options: RedirectOptions = { state: { returnTo: suiteReturnPath(requestedPath, products[product].home) }, ...(rejected ? { prompt: 'login' as const } : {}) };
  const flight = Promise.resolve().then(() => redirect(options)).then(() => undefined)
    .catch(error => { throw error instanceof Error ? error : new Error('Unable to sign in. Please try again.'); })
    .finally(() => { pending.delete(product); });
  pending.set(product, flight);
  return flight;
}
